import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Button, Input, Select, Tabs, Switch, Tag, Space, message, Spin,
  Modal, Tooltip, ColorPicker, InputNumber, DatePicker
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, EyeOutlined, PlusOutlined,
  DeleteOutlined, CopyOutlined, ArrowUpOutlined, ArrowDownOutlined,
  SettingOutlined, BgColorsOutlined, FormOutlined, CheckCircleOutlined,
  LinkOutlined
} from '@ant-design/icons';
import SurveyService from '../../../services/SurveyService';
import ROUTES from '../../../constants/routes';
import PublicSurveyPage from '../../PublicSurvey';
import {
  BuilderContainer, TopBar, ContentArea, QuestionCard, QuestionHeader,
  AddQuestionBar, BrandingCard, TabsContainer
} from './styled';

const { TextArea } = Input;

const QUESTION_TYPES = [
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text (Paragraph)' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'rating', label: 'Star Rating' },
  { value: 'scale', label: 'Numeric Scale (NPS)' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' }
];

const newQuestion = (order) => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  type: 'short_text',
  question: '',
  description: '',
  required: false,
  order,
  options: []
});

const newOption = () => ({
  id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  label: '',
  value: ''
});

const defaultBranding = {
  primaryColor: '#E31B23',
  backgroundColor: '#ffffff',
  textColor: '#262626',
  buttonColor: '#E31B23',
  buttonTextColor: '#ffffff',
  logoUrl: '',
  fontFamily: 'Inter, sans-serif',
  showProgressBar: true,
  thankYouMessage: 'Thank you for your response!',
  thankYouImageUrl: ''
};

const defaultSettings = {
  accessType: 'public',
  accessCode: '',
  allowMultipleResponses: true,
  maxResponses: null,
  startDate: null,
  endDate: null,
  showQuestionNumbers: true,
  oneQuestionPerPage: false,
  randomizeQuestions: false,
  publicPageEnabled: true,
  collectRespondentInfo: true,
  redirectUrl: '',
  notifyOnResponse: false,
  notificationEmails: []
};

const SurveyBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [survey, setSurvey] = useState({
    title: 'Untitled Survey',
    description: '',
    questions: [],
    branding: defaultBranding,
    settings: defaultSettings,
    status: 'draft'
  });
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [activeTab, setActiveTab] = useState('questions');
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!isNew) {
      (async () => {
        const result = await SurveyService.getSurvey(id);
        if (result.success) {
          setSurvey({
            ...result.data,
            branding: { ...defaultBranding, ...(result.data.branding || {}) },
            settings: { ...defaultSettings, ...(result.data.settings || {}) }
          });
        } else {
          message.error(result.error);
        }
        setLoading(false);
      })();
    }
  }, [id, isNew]);

  const updateSurvey = (patch) => setSurvey((s) => ({ ...s, ...patch }));
  const updateBranding = (patch) => setSurvey((s) => ({ ...s, branding: { ...s.branding, ...patch } }));
  const updateSettings = (patch) => setSurvey((s) => ({ ...s, settings: { ...s.settings, ...patch } }));

  const updateQuestion = (qid, patch) => {
    setSurvey((s) => ({
      ...s,
      questions: s.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q))
    }));
  };

  const addQuestion = () => {
    setSurvey((s) => {
      const q = newQuestion(s.questions.length);
      setActiveQuestionId(q.id);
      return { ...s, questions: [...s.questions, q] };
    });
  };

  const deleteQuestion = (qid) => {
    setSurvey((s) => ({
      ...s,
      questions: s.questions
        .filter((q) => q.id !== qid)
        .map((q, idx) => ({ ...q, order: idx }))
    }));
  };

  const duplicateQuestion = (qid) => {
    setSurvey((s) => {
      const idx = s.questions.findIndex((q) => q.id === qid);
      if (idx === -1) return s;
      const original = s.questions[idx];
      const copy = { ...original, id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` };
      const next = [...s.questions];
      next.splice(idx + 1, 0, copy);
      return { ...s, questions: next.map((q, i) => ({ ...q, order: i })) };
    });
  };

  const moveQuestion = (qid, direction) => {
    setSurvey((s) => {
      const idx = s.questions.findIndex((q) => q.id === qid);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= s.questions.length) return s;
      const next = [...s.questions];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return { ...s, questions: next.map((q, i) => ({ ...q, order: i })) };
    });
  };

  const addOption = (qid) => {
    setSurvey((s) => ({
      ...s,
      questions: s.questions.map((q) => q.id === qid
        ? { ...q, options: [...(q.options || []), newOption()] }
        : q)
    }));
  };

  const updateOption = (qid, optId, patch) => {
    setSurvey((s) => ({
      ...s,
      questions: s.questions.map((q) => q.id === qid
        ? { ...q, options: q.options.map((o) => (o.id === optId ? { ...o, ...patch } : o)) }
        : q)
    }));
  };

  const deleteOption = (qid, optId) => {
    setSurvey((s) => ({
      ...s,
      questions: s.questions.map((q) => q.id === qid
        ? { ...q, options: q.options.filter((o) => o.id !== optId) }
        : q)
    }));
  };

  const handleSave = useCallback(async (newStatus) => {
    // Validate that every question has text — the backend schema requires it.
    const emptyIdx = survey.questions.findIndex((q) => !q.question || !q.question.trim());
    if (emptyIdx !== -1) {
      message.warning(`Question ${emptyIdx + 1} is missing text. Please fill it in or delete it before saving.`);
      return;
    }

    setSaving(true);
    const payload = { ...survey };
    if (newStatus) payload.status = newStatus;

    let result;
    if (isNew) {
      result = await SurveyService.createSurvey(payload);
      if (result.success) {
        message.success('Survey created');
        navigate(`/admin/surveys/${result.data._id}/edit`, { replace: true });
      }
    } else {
      result = await SurveyService.updateSurvey(id, payload);
      if (result.success) {
        message.success('Survey saved');
        setSurvey((s) => ({ ...s, status: result.data.status }));
      }
    }
    if (!result.success) message.error(result.error);
    setSaving(false);
  }, [survey, isNew, id, navigate]);

  const handlePublish = () => {
    if (survey.questions.length === 0) {
      message.warning('Add at least one question before publishing');
      return;
    }
    Modal.confirm({
      title: 'Publish this survey?',
      content: 'Once published, the public link will accept responses.',
      okText: 'Publish',
      onOk: () => handleSave('active')
    });
  };

  const handleClose = () => {
    Modal.confirm({
      title: 'Close this survey?',
      content: 'No new responses will be accepted, but existing responses are preserved.',
      okText: 'Close Survey',
      onOk: () => handleSave('closed')
    });
  };

  if (loading) {
    return (
      <BuilderContainer>
        <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
      </BuilderContainer>
    );
  }

  const showOptionsEditor = (q) => ['single_choice', 'multiple_choice', 'dropdown'].includes(q.type);
  const showScaleEditor = (q) => q.type === 'scale';

  return (
    <BuilderContainer>
      <TopBar>
        <Space size="middle">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.ADMIN_SURVEYS)}>
            Back
          </Button>
          <Input
            className="title-input"
            value={survey.title}
            onChange={(e) => updateSurvey({ title: e.target.value })}
            placeholder="Untitled Survey"
          />
          <Tag color={survey.status === 'active' ? 'green' : 'default'}>
            {survey.status?.toUpperCase() || 'DRAFT'}
          </Tag>
        </Space>
        <Space>
          {!isNew && survey.slug && (
            <Tooltip title="Copy public link">
              <Button
                icon={<LinkOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/s/${survey.slug}`);
                  message.success('Link copied');
                }}
              >
                {survey.slug}
              </Button>
            </Tooltip>
          )}
          <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
            Preview
          </Button>
          <Button icon={<SaveOutlined />} onClick={() => handleSave()} loading={saving}>
            Save Draft
          </Button>
          {survey.status !== 'active' ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handlePublish}
              loading={saving}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Publish
            </Button>
          ) : (
            <Button danger onClick={handleClose} loading={saving}>
              Close Survey
            </Button>
          )}
        </Space>
      </TopBar>

      <TabsContainer>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'questions', label: <span><FormOutlined /> Questions</span> },
            { key: 'branding', label: <span><BgColorsOutlined /> Branding</span> },
            { key: 'settings', label: <span><SettingOutlined /> Settings</span> }
          ]}
        />
      </TabsContainer>

      <ContentArea>
        <div className="builder-inner">
        {activeTab === 'questions' && (
          <>
            <BrandingCard>
              <h3>Description</h3>
              <TextArea
                rows={2}
                placeholder="Optional intro text shown above the questions"
                value={survey.description}
                onChange={(e) => updateSurvey({ description: e.target.value })}
              />
            </BrandingCard>

            {survey.questions.map((q, idx) => (
              <QuestionCard
                key={q.id}
                $active={activeQuestionId === q.id}
                onClick={() => setActiveQuestionId(q.id)}
              >
                <QuestionHeader>
                  <span className="number">Q{idx + 1}</span>
                  <Select
                    value={q.type}
                    onChange={(type) => updateQuestion(q.id, { type })}
                    options={QUESTION_TYPES}
                    style={{ width: 220 }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Switch
                    checked={q.required}
                    onChange={(required) => updateQuestion(q.id, { required })}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                  />
                  <div style={{ flex: 1 }} />
                  <Tooltip title="Move up">
                    <Button size="small" type="text" icon={<ArrowUpOutlined />}
                      onClick={(e) => { e.stopPropagation(); moveQuestion(q.id, -1); }} />
                  </Tooltip>
                  <Tooltip title="Move down">
                    <Button size="small" type="text" icon={<ArrowDownOutlined />}
                      onClick={(e) => { e.stopPropagation(); moveQuestion(q.id, 1); }} />
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <Button size="small" type="text" icon={<CopyOutlined />}
                      onClick={(e) => { e.stopPropagation(); duplicateQuestion(q.id); }} />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}
                      onClick={(e) => { e.stopPropagation(); deleteQuestion(q.id); }} />
                  </Tooltip>
                </QuestionHeader>

                <Input
                  placeholder="Enter your question..."
                  value={q.question}
                  onChange={(e) => updateQuestion(q.id, { question: e.target.value })}
                  style={{ marginBottom: 8, fontSize: 16 }}
                  size="large"
                  onClick={(e) => e.stopPropagation()}
                />
                <Input
                  placeholder="Description / help text (optional)"
                  value={q.description}
                  onChange={(e) => updateQuestion(q.id, { description: e.target.value })}
                  style={{ marginBottom: 12 }}
                  onClick={(e) => e.stopPropagation()}
                />

                {showOptionsEditor(q) && (
                  <div style={{ paddingLeft: 16, borderLeft: '3px solid #f0f0f0' }}>
                    {(q.options || []).map((opt) => (
                      <div key={opt.id} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <Input
                          placeholder="Option label"
                          value={opt.label}
                          onChange={(e) => updateOption(q.id, opt.id, { label: e.target.value, value: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => { e.stopPropagation(); deleteOption(q.id, opt.id); }}
                        />
                      </div>
                    ))}
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={(e) => { e.stopPropagation(); addOption(q.id); }}
                    >
                      Add Option
                    </Button>
                  </div>
                )}

                {showScaleEditor(q) && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <span>From</span>
                    <InputNumber
                      value={q.scaleMin ?? 0}
                      onChange={(v) => updateQuestion(q.id, { scaleMin: v })}
                    />
                    <Input
                      placeholder="Low label (e.g. Not at all)"
                      value={q.scaleMinLabel}
                      onChange={(e) => updateQuestion(q.id, { scaleMinLabel: e.target.value })}
                      style={{ width: 200 }}
                    />
                    <span>To</span>
                    <InputNumber
                      value={q.scaleMax ?? 10}
                      onChange={(v) => updateQuestion(q.id, { scaleMax: v })}
                    />
                    <Input
                      placeholder="High label (e.g. Very much)"
                      value={q.scaleMaxLabel}
                      onChange={(e) => updateQuestion(q.id, { scaleMaxLabel: e.target.value })}
                      style={{ width: 200 }}
                    />
                  </div>
                )}
              </QuestionCard>
            ))}

            <AddQuestionBar onClick={addQuestion}>
              <PlusOutlined /> Add Question
            </AddQuestionBar>
          </>
        )}

        {activeTab === 'branding' && (
          <>
            <BrandingCard>
              <h3>Colors</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <ColorRow label="Primary Color" value={survey.branding.primaryColor}
                  onChange={(c) => updateBranding({ primaryColor: c })} />
                <ColorRow label="Background" value={survey.branding.backgroundColor}
                  onChange={(c) => updateBranding({ backgroundColor: c })} />
                <ColorRow label="Text Color" value={survey.branding.textColor}
                  onChange={(c) => updateBranding({ textColor: c })} />
                <ColorRow label="Button Color" value={survey.branding.buttonColor}
                  onChange={(c) => updateBranding({ buttonColor: c })} />
                <ColorRow label="Button Text" value={survey.branding.buttonTextColor}
                  onChange={(c) => updateBranding({ buttonTextColor: c })} />
              </div>
            </BrandingCard>

            <BrandingCard>
              <h3>Logo & Typography</h3>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 6 }}>Logo URL</label>
                <Input
                  placeholder="https://..."
                  value={survey.branding.logoUrl}
                  onChange={(e) => updateBranding({ logoUrl: e.target.value })}
                />
                {survey.branding.logoUrl && (
                  <img src={survey.branding.logoUrl} alt="Logo preview" style={{ marginTop: 8, maxHeight: 60 }} />
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6 }}>Font Family</label>
                <Select
                  value={survey.branding.fontFamily}
                  style={{ width: '100%' }}
                  onChange={(v) => updateBranding({ fontFamily: v })}
                  options={[
                    { value: 'Inter, sans-serif', label: 'Inter (Default)' },
                    { value: 'Arial, sans-serif', label: 'Arial' },
                    { value: 'Georgia, serif', label: 'Georgia' },
                    { value: '"Times New Roman", serif', label: 'Times New Roman' },
                    { value: '"Helvetica Neue", sans-serif', label: 'Helvetica Neue' },
                    { value: 'system-ui, sans-serif', label: 'System Default' }
                  ]}
                />
              </div>
              <div style={{ marginTop: 16 }}>
                <Switch
                  checked={survey.branding.showProgressBar}
                  onChange={(v) => updateBranding({ showProgressBar: v })}
                /> <span style={{ marginLeft: 8 }}>Show progress bar</span>
              </div>
            </BrandingCard>

            <BrandingCard>
              <h3>Thank You Page</h3>
              <label style={{ display: 'block', marginBottom: 6 }}>Thank You Message</label>
              <TextArea
                rows={3}
                value={survey.branding.thankYouMessage}
                onChange={(e) => updateBranding({ thankYouMessage: e.target.value })}
                style={{ marginBottom: 12 }}
              />
              <label style={{ display: 'block', marginBottom: 6 }}>Thank You Image URL (optional)</label>
              <Input
                placeholder="https://..."
                value={survey.branding.thankYouImageUrl}
                onChange={(e) => updateBranding({ thankYouImageUrl: e.target.value })}
              />
            </BrandingCard>
          </>
        )}

        {activeTab === 'settings' && (
          <>
            <BrandingCard>
              <h3>Access Control</h3>
              <Select
                value={survey.settings.accessType}
                style={{ width: '100%', marginBottom: 12 }}
                onChange={(v) => updateSettings({ accessType: v })}
                options={[
                  { value: 'public', label: 'Public — Anyone with the link' },
                  { value: 'code', label: 'Code Required — Respondents need an access code' }
                ]}
              />
              {survey.settings.accessType === 'code' && (
                <Input
                  placeholder="Access code"
                  value={survey.settings.accessCode}
                  onChange={(e) => updateSettings({ accessCode: e.target.value })}
                  style={{ marginBottom: 12 }}
                />
              )}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 6 }}>Max Responses (optional)</label>
                <InputNumber
                  min={1}
                  value={survey.settings.maxResponses}
                  onChange={(v) => updateSettings({ maxResponses: v })}
                  style={{ width: 200 }}
                  placeholder="Unlimited"
                />
              </div>
            </BrandingCard>

            <BrandingCard>
              <h3>Display Options</h3>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.publicPageEnabled !== false}
                  onChange={(v) => updateSettings({ publicPageEnabled: v })}
                /> <span style={{ marginLeft: 8 }}>Show the public survey page</span>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 44 }}>
                  When off, visitors to the survey link see an &quot;unavailable&quot; message.
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.collectRespondentInfo !== false}
                  onChange={(v) => updateSettings({ collectRespondentInfo: v })}
                /> <span style={{ marginLeft: 8 }}>Collect respondent info (name, email, phone)</span>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 44 }}>
                  When off, the optional &quot;Your Information&quot; step is hidden entirely.
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.showQuestionNumbers}
                  onChange={(v) => updateSettings({ showQuestionNumbers: v })}
                /> <span style={{ marginLeft: 8 }}>Show question numbers</span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.oneQuestionPerPage}
                  onChange={(v) => updateSettings({ oneQuestionPerPage: v })}
                /> <span style={{ marginLeft: 8 }}>One question per page</span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.randomizeQuestions}
                  onChange={(v) => updateSettings({ randomizeQuestions: v })}
                /> <span style={{ marginLeft: 8 }}>Randomize question order</span>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.allowMultipleResponses}
                  onChange={(v) => updateSettings({ allowMultipleResponses: v })}
                /> <span style={{ marginLeft: 8 }}>Allow multiple responses from the same person</span>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 44 }}>
                  When off, the same email or device cannot submit twice.
                </div>
              </div>
            </BrandingCard>

            <BrandingCard>
              <h3>Schedule</h3>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6 }}>Start Date (optional)</label>
                  <DatePicker
                    showTime
                    value={survey.settings.startDate ? dayjs(survey.settings.startDate) : null}
                    onChange={(d) => updateSettings({ startDate: d ? d.toISOString() : null })}
                    placeholder="Available immediately"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6 }}>End Date (optional)</label>
                  <DatePicker
                    showTime
                    value={survey.settings.endDate ? dayjs(survey.settings.endDate) : null}
                    onChange={(d) => updateSettings({ endDate: d ? d.toISOString() : null })}
                    placeholder="No end date"
                  />
                </div>
              </div>
            </BrandingCard>

            <BrandingCard>
              <h3>Notifications</h3>
              <div style={{ marginBottom: 12 }}>
                <Switch
                  checked={survey.settings.notifyOnResponse}
                  onChange={(v) => updateSettings({ notifyOnResponse: v })}
                /> <span style={{ marginLeft: 8 }}>Email me when a response is submitted</span>
              </div>
              {survey.settings.notifyOnResponse && (
                <>
                  <label style={{ display: 'block', marginBottom: 6 }}>Notification Emails</label>
                  <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Add email addresses (press Enter)"
                    value={survey.settings.notificationEmails || []}
                    onChange={(v) => updateSettings({ notificationEmails: v })}
                    tokenSeparators={[',', ' ']}
                  />
                </>
              )}
            </BrandingCard>

            <BrandingCard>
              <h3>After Submission</h3>
              <label style={{ display: 'block', marginBottom: 6 }}>Redirect URL (optional)</label>
              <Input
                placeholder="https://..."
                value={survey.settings.redirectUrl}
                onChange={(e) => updateSettings({ redirectUrl: e.target.value })}
              />
            </BrandingCard>
          </>
        )}
        </div>
      </ContentArea>

      <Modal
        title="Survey Preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={820}
        bodyStyle={{ padding: 0, background: '#f5f5f5', maxHeight: '75vh', overflowY: 'auto' }}
        destroyOnClose
      >
        <PublicSurveyPage previewMode surveyData={survey} />
      </Modal>
    </BuilderContainer>
  );
};

const ColorRow = ({ label, value, onChange }) => (
  <div>
    <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>{label}</label>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <ColorPicker
        value={value}
        onChange={(c) => onChange(c.toHexString())}
        showText
      />
    </div>
  </div>
);

export default SurveyBuilder;
